/**
 * Legacy read adapter (SC, owner decision 2026-10-01): a quick-study monadic
 * scorecard stored before Python stamped `scorecard_plot`, read as a `radar`
 * from its stored aggregate (`monadic_scorecard`), so the stored radar view
 * draws `RadarPlot` instead of the deprecated `RadarChart`.
 *
 * Shape only, the documented legacy exception (read-adapters.md, as decision
 * 22): dimensions are the axes, each item one series. A value is the item's
 * stored mean, as stored (never rounded); a mean that is not a number stays
 * null (a gap, never 0). Colour slots follow the item's stored position. The
 * stored shape carries no low-base flag, so no series draws one (today's
 * reader drew none). The scale is the first item's stored bounds, 1 to 5 when
 * absent (today's reader). Fewer than three dimensions or no item: not read
 * (today's floor), the stored heatmap stays the only view.
 *
 * Never used for a newly built payload; deleted with the last stored scorecard.
 */

import { PlotReadAdapter, StoredPlotPayload } from '../../contract'

type StoredRadar = StoredPlotPayload<'radar'>

/** Today's reader printed scorecard radar means at two decimals (`toFixed(2)`). */
const LEGACY_SCORECARD_RADAR_DECIMALS = 2
const PALETTE_SLOTS = 8
const MIN_AXES = 3

interface StoredScorecardItem {
  id?: unknown
  label?: unknown
  mean?: unknown
  scale_min?: unknown
  scale_max?: unknown
}

interface StoredScorecardDimension {
  dimension?: unknown
  higher_is_better?: unknown
  items?: Array<StoredScorecardItem>
}

/** A quick-study result as stored, `monadic_scorecard` optional. */
export interface StoredQuickStudyScorecardRadar {
  monadic_scorecard?: Array<StoredScorecardDimension> | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function dimensionsOf(stored: unknown): Array<StoredScorecardDimension> {
  if (!isRecord(stored) || !Array.isArray(stored.monadic_scorecard)) {
    return []
  }
  return stored.monadic_scorecard.filter(isRecord) as Array<StoredScorecardDimension>
}

export const quickStudyScorecardRadarAdapter: PlotReadAdapter<'radar', StoredQuickStudyScorecardRadar> = {
  kind: 'radar',
  source: { surface: 'quick-study', shape: 'monadic_scorecard' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredQuickStudyScorecardRadar {
    const dimensions = dimensionsOf(stored)
    return dimensions.length >= MIN_AXES && Array.isArray(dimensions[0].items) && dimensions[0].items.length > 0
  },
  read(stored: StoredQuickStudyScorecardRadar): StoredRadar {
    const dimensions = dimensionsOf(stored)
    const items = (dimensions[0].items ?? []).filter(isRecord)
    const first = items[0] ?? {}
    return {
      kind: 'radar',
      data: {
        axes: dimensions.map((dimension, index) => ({
          id: String(dimension.dimension ?? index),
          label: String(dimension.dimension ?? ''),
          higherIsBetter: dimension.higher_is_better !== false,
        })),
        series: items.map((item, index) => ({
          id: String(item.id ?? index),
          label: String(item.label || item.id || ''),
          colorSlot: (index % PALETTE_SLOTS) + 1,
          lowBase: false,
          base: null,
          // Joined by item id, else the item's position (today's reader).
          values: dimensions.map((dimension) => {
            const cell = (dimension.items ?? []).find((entry) => isRecord(entry) && entry.id === item.id) ?? dimension.items?.[index]
            return finite(cell?.mean)
          }),
        })),
        scale: { min: finite(first.scale_min) ?? 1, max: finite(first.scale_max) ?? 5 },
        decimals: LEGACY_SCORECARD_RADAR_DECIMALS,
        labels: {},
      },
    }
  },
}
